import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonVariant } from './Button';
import styles from './ConfirmDialog.module.css';

interface ConfirmDialogProps {
  readonly title: string;
  readonly children: ReactNode;
  /** Omitted for a question with no default action: every choice is then an extra action. */
  readonly confirmLabel?: string;
  /** Text of the cancel button; defaults to "Cancel" (e.g. "Keep editing" for a discard question). */
  readonly cancelLabel?: string;
  /** Styles the confirm button as destructive. */
  readonly danger?: boolean;
  /** When set, the confirm button stays disabled until the user types this word. */
  readonly confirmWord?: string;
  /** Keeps the confirm button disabled while the dialog's own inputs are incomplete. */
  readonly confirmDisabled?: boolean;
  /** Disables the buttons while the confirmed action runs. */
  readonly busy?: boolean;
  /** Extra buttons between Cancel and Confirm (e.g. "export first"). */
  readonly extraActions?: ReactNode;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly testId?: string;
}

/** A modal question on a native `<dialog>`: focus trap, Esc to cancel, optional typed confirmation. */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  cancelLabel,
  danger = false,
  confirmWord,
  confirmDisabled = false,
  busy = false,
  extraActions,
  onConfirm,
  onCancel,
  testId = 'confirm-dialog',
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [typed, setTyped] = useState('');

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
    return () => {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    };
  }, []);

  const wordMatches = confirmWord === undefined || typed.trim().toLowerCase() === confirmWord.toLowerCase();

  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      aria-labelledby={titleId}
      data-testid={testId}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onCancel();
      }}
    >
      <form
        className={styles.content}
        onSubmit={(event) => {
          event.preventDefault();
          if (wordMatches && !confirmDisabled && !busy) onConfirm();
        }}
      >
        <h2 id={titleId} className={styles.title}>
          {title}
        </h2>
        <div className={styles.body}>{children}</div>
        {confirmWord === undefined ? null : (
          <label className={styles.word}>
            <span>{t('settings.confirm.typeWord', { word: confirmWord })}</span>
            <input
              value={typed}
              autoComplete="off"
              autoFocus
              onChange={(event) => setTyped(event.target.value)}
              data-testid="confirm-word-input"
            />
          </label>
        )}
        <div className={styles.actions}>
          <Button disabled={busy} onClick={onCancel} data-testid="confirm-cancel">
            {cancelLabel ?? t('settings.confirm.cancel')}
          </Button>
          {extraActions}
          {confirmLabel === undefined ? null : (
            <Button
              type="submit"
              variant={danger ? ButtonVariant.Danger : ButtonVariant.Primary}
              disabled={!wordMatches || confirmDisabled || busy}
              data-testid="confirm-ok"
            >
              {confirmLabel}
            </Button>
          )}
        </div>
      </form>
    </dialog>
  );
}
